/**
 * Mobile Petty Cash — My Expenses list.
 * Shows the current user's submitted requests with status filter chips.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ListChecks, MapPin, PlusCircle } from 'lucide-react';
import { apiGet } from '../../lib/api';
import { formatINR, formatDate } from '../../lib/format';
import { useAuth } from '../../contexts/AuthContext';

const STATUS_CLASSES = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200',
};

export default function MobilePettyCashList() {
  const { user } = useAuth() || {};
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    const submittedBy = user.id || user.email;
    const params = { submitted_by: submittedBy, limit: 200 };
    if (filter !== 'all') params.status = filter;
    apiGet('/petty-cash/requests', params)
      .then((d) => setItems(d?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [user, filter]);

  return (
    <div className="min-h-screen bg-slate-50 pb-6" data-testid="m-petty-cash-list">
      <div className="bg-[#1F7A6E] text-white px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Link to="/m/petty-cash" className="p-1.5 -ml-1.5"><ArrowLeft size={20} /></Link>
        <div className="flex-1">
          <div className="font-display font-bold flex items-center gap-1.5"><ListChecks size={14} /> My Expenses</div>
          <div className="text-[10px] text-slate-300">Status &amp; history</div>
        </div>
        <Link to="/m/petty-cash/expense" className="p-1.5 -mr-1.5" data-testid="m-pc-list-new"><PlusCircle size={20} /></Link>
      </div>

      <div className="px-4 pt-3 flex gap-1.5 overflow-x-auto" data-testid="m-pc-list-filter">
        {['all', 'pending', 'approved', 'rejected'].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`m-pc-filter-${s}`}
            className={`px-3 py-1.5 rounded-full text-[11px] font-display font-bold uppercase tracking-widest whitespace-nowrap border ${
              filter === s ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : 'bg-white text-slate-600 border-slate-300'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="p-4 space-y-2">
        {loading ? (
          <div className="text-center text-slate-500 text-sm py-6">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center text-slate-500 text-sm py-12">
            No expenses yet.<br />
            <Link to="/m/petty-cash/expense" className="text-teal-600 underline mt-2 inline-block">Submit your first expense</Link>
          </div>
        ) : items.map((r) => (
          <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-3" data-testid={`m-pc-row-${r.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold text-sm text-slate-900 truncate">{r.vendor_name || '—'}</div>
                <div className="text-[11px] text-slate-500 truncate">{r.project_name || '—'} · {r.category}</div>
              </div>
              <span className={`px-2 py-0.5 rounded border text-[10px] font-display font-bold uppercase tracking-widest ${STATUS_CLASSES[r.status] || 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                {r.status}
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px] text-slate-600">
              <span>{formatDate(r.expense_date)} · {r.payment_mode}</span>
              <span className="font-display font-bold text-slate-900">{formatINR(r.actual_amount || r.estimated_amount || 0)}</span>
            </div>
            {/* Iter 140 — the AI risk score is intentionally NOT shown to
                the submitter; only geofence breaches are surfaced here
                because the user genuinely needs to know their submission
                will need a project-head override. */}
            {r.geofence_outside && (
              <div className="mt-1.5 text-[10px] flex items-center gap-1 text-rose-600">
                <MapPin size={10} /> outside geofence
              </div>
            )}
            {r.status === 'rejected' && r.reject_reason && (
              <div className="mt-1.5 text-[10px] text-rose-600 italic">Reason: {r.reject_reason}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
